import React, { useState, useEffect, useRef } from "react";
import Recents from "./Cards/recents.js";
import HeadtoHead from "./Cards/headtohead.js";
import Score from "./Cards/score.js";
import Versus from "./Cards/versus.js";
import Totals from "./Cards/totals.js";
import PointDiff from "./Cards/pointdiff.js";
import Overs from "./Cards/overs.js";
import Wins from "./Cards/wins.js";
import Spreads from "./Cards/spreads.js";
import Projections from "./Cards/projections.js";
import Card from "react-bootstrap/Card";
import Comments from "./Cards/comments.js";
import OddsTable from "./OddsTable.js";
import Suggestions from "./Cards/suggestions.js";
import { convertUTCDateToLocalDateTime } from "../utils/localtime.js";
import { styles } from "../styles/styles.js";
import { useAppState } from "../AppStateContext.js";
import { useLocation } from "react-router-dom";
import createScrollSnap from "scroll-snap";
import { useParams } from 'react-router-dom';
import { useHapticNavigate as useNavigate, hapticTap } from '../utils/haptics';
import * as motion from "motion/react-client";
import axios from 'axios';
import SuperSummary from './Cards/supersummary.js';
import Verdict from './Cards/verdict.js';
import ErrorBoundary from './ErrorBoundary.js';
import { isV2 } from '../utils/picks.js';
import Pitchers from "./Cards/pitchers.js";
import LineMovement from "./Cards/linemovement.js";
import { pacificSlateDate, slateHref } from "../utils/slateDate";


const CardButton = ({ title, onClick, index = 0 }) => (
  <motion.div
    initial={{ opacity: 0, x: -100 }}
    animate={{ opacity: 1, x: 0}}
    transition={{
      duration: 0.5,
      delay: index * 0.15, 
      scale: { type: "spring", damping: 10, stiffness: 100, bounce: 0.5 }
    }}
  >
    <Card
      onClick={() => { hapticTap(); if (onClick) onClick(); }}
      className="mb-3 game-card"
      style={{...styles.cardbutton, width: '100%'}}
    >
      <Card.Body>
        <Card.Title style={{ color: 'white', margin: 0, fontSize: 'clamp(12px, calc(12px + (4) * ((100vw - 300px) / (1200 - 320))), 18px)' }}>{title}</Card.Title>
      </Card.Body>
    </Card>
  </motion.div>
);

const Games = ({ isAuthenticated }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams(); 
  const simple = new URLSearchParams(location.search).get('simple');

  const [fixture, setFixture] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [isDataVisible, setDataVisible] = useState(false);
  const [isRecentsHomeVisible, setRecentsHomeVisible] = useState(false);
  const [isRecentsAwayVisible, setRecentsAwayVisible] = useState(false);
  const [isHeadtoHeadVisible, setHeadtoHeadVisible] = useState(false);
  const [isPowerRatingsVisible, setPowerRatingsVisible] = useState(false);
  const [isButtonListVisible, setButtonListVisible] = useState(true);
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [commentCount, setCommentCount] = useState(0);
  const [isLineMovementVisible, setLineMovementVisible] = useState(false);

  useEffect(() => {
    // Set when `id` changes or the page unmounts, so a slow response for the
    // PREVIOUS fixture cannot land on top of the one now being shown.
    let cancelled = false;

    const loadFixture = async () => {
      if (!id) return;

      // STALE-WHILE-REVALIDATE. Show the cached copy instantly, then always fetch.
      //
      // This used to `return` on a cache hit, so a fixture opened before its AI
      // summary existed kept rendering that summary-less copy for the cache's full
      // hour -- no Refresh anywhere could reach it, because the slate's Refresh
      // refetches the slate, not this. Reproduced twice on prod 2026-09-14: the API
      // was already serving the finished box_score while this page showed none.
      const cacheKey = `fixture_detail_${id}`;
      const cached = getWithExpiry(cacheKey);
      if (cached) {
        setFixture(cached);
      } else if (location.state?.fixture) {
        // The light list payload is only a placeholder. Never let it overwrite a
        // richer cached copy -- it carries no box_score, insights or line_history.
        setFixture(location.state.fixture);
      }

      // Always fetch full fixture data by ID
      try {
        const url = process.env.REACT_APP_API_URL;
        const token = localStorage.getItem('authToken');
        const headers = token ? { Authorization: `Token ${token}` } : {};

        const { data: response } = await axios.get(`${url}/getFixtures/`, {
          params: { id },
          headers: headers
        });

        if (cancelled) return;
        if (response && response.length > 0) {
          setFixture(response[0]);
          setWithExpiry(cacheKey, response[0]);
        } else {
          setLoadError('missing');
        }
      } catch (error) {
        if (cancelled) return;
        // Both the failure path and the empty-response path used to leave
        // `fixture` null, and the render below shows "Loading..." for a null
        // fixture — so a dead backend and a fixture that does not exist both
        // presented as a page that is still loading, forever.
        console.error('Error fetching fixture:', error);
        setLoadError('error');
      }
    };

    loadFixture();
    return () => { cancelled = true; };
  }, [id]); // Only depend on id

  const { isTutorialMode, isDesktopMode, userTier, toggleTutorialMode, getWithExpiry, setWithExpiry } = useAppState();
  const containerRef = useRef(null);

  const isSignedIn = isAuthenticated && (userTier === 'Pro' || userTier === 'Free');


  const toggleDataVisibility = () => {
    setDataVisible(!isDataVisible);
    setButtonListVisible(!isButtonListVisible);
  };

  const toggleRecentsHomeVisibility = () => {
    setRecentsHomeVisible(!isRecentsHomeVisible);
    setButtonListVisible(!isButtonListVisible);
  };

  const toggleRecentsAwayVisibility = () => {
    setRecentsAwayVisible(!isRecentsAwayVisible);
    setButtonListVisible(!isButtonListVisible);
  };

  const toggleHeadtoHeadVisibility = () => {
    setHeadtoHeadVisible(!isHeadtoHeadVisible);
    setButtonListVisible(!isButtonListVisible);
  };

  const toggleLineMovementVisibility = () => {
    setLineMovementVisible(!isLineMovementVisible);
    setButtonListVisible(!isButtonListVisible);
  };

  const togglePowerRatingsVisibility = () => {
    setPowerRatingsVisible(!isPowerRatingsVisible);
    setButtonListVisible(!isButtonListVisible);
  };

  const resetAll = () => {
    console.log('Resetting all');
    setDataVisible(false);
    setRecentsHomeVisible(false);
    setRecentsAwayVisible(false);
    setHeadtoHeadVisible(false);
    setPowerRatingsVisible(false);
    setButtonListVisible(true);
    setLineMovementVisible(false);
  };

  const handleBackToGames = () => {
    // Back to the slate this fixture was opened from. That day now lives in the
    // URL; this used to pass `state: { selectedDate }`, which nothing ever read.
    // The fallback is the PACIFIC day of the kickoff, because the backend's slate
    // window is Pacific midnight for every league -- a browser-local date files a
    // late Pacific game on a slate that does not list it.
    const slateDate = location.state?.slateDate || pacificSlateDate(fixture.game_time);
    navigate(slateHref(fixture.league, slateDate));
  };

  useEffect(() => {
    const getCommentCount = async () => {
      if (!fixture?.id) return;
      
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/comments/count_comments/`, {
          params: { fixture: fixture.id }
        });
        setCommentCount(response.data.count);
      } catch (error) {
        console.error('Error fetching comment count:', error);
      }
    };

    getCommentCount();
}, [fixture?.id]); 

  
  
  useEffect(() => {
    if (isDesktopMode) return;
    const container = containerRef.current;
    if (container) {
      const { bind, unbind } = createScrollSnap(
        container,
        {
          snapDestinationY: "0%",
          timeout: 100,
          duration: 300,
          threshold: 0.2,
          snapStop: false,
          easing: (t) => t * (2 - t),
        },
        () => console.log("Element snapped")
      );

      bind();
      return () => unbind();
    }
  }, [isDesktopMode]);

  if (!fixture) {
    return (
      <div>
        <Card className="main" style={styles.main}>
          <Card.Body>
            <Card.Title>
              {loadError === 'error'
                ? "Couldn't load this game."
                : loadError === 'missing'
                  ? 'Game not found.'
                  : 'Loading...'}
            </Card.Title>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // A v2 game (librarian B29 P2.6) shows the verdict card in place of v1's Suggestions and
  // SuperSummary. The detail's box_score says so; while the slate's placeholder is up
  // (no box_score), its plays do, so a v2 game never flashes v1's cards.
  const isV2Game = fixture.box_score?.pipeline_version === 2
    || (Array.isArray(fixture.suggestions) ? fixture.suggestions : []).some(isV2);


  if (simple === "true") {
    return (
      <div>
        <Card className="main" style={styles.main}>
          <Card.Body>
            <Card.Title>
              <b>
                <Versus fixture={fixture} /> at {convertUTCDateToLocalDateTime(fixture.game_time)}
              </b>
            </Card.Title>
            <OddsTable fixture={fixture} onClick={resetAll} />
            <ErrorBoundary name="verdict" resetKey={fixture}>
              <Verdict fixture={fixture} signedIn={isSignedIn} />
            </ErrorBoundary>
            <Wins fixture={fixture} />
            <Spreads fixture={fixture} />
            <PointDiff fixture={fixture} />
            <Totals fixture={fixture} />
            <Overs fixture={fixture} />
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (isDesktopMode) {
    const renderProjections = () => {
      if (isSignedIn) {
        return <Projections fixture={fixture} onClose={null} />;
      }
      return (
        <div style={{ marginBottom: "20px", textAlign: "center", padding: "20px" }}>
          <p style={styles.analysis}>Create a free account to unlock projections!</p>
          <Card className="cardbutton" style={{...styles.cardbutton, width: '100%', marginBottom: "20px"}} onClick={() => navigate('/register')}>
            <Card.Text>Sign Up</Card.Text>
          </Card>
          <Card className="cardbutton" style={{...styles.cardbutton, width: '100%', marginBottom: "20px"}} onClick={() => navigate('/signin')}>
            <Card.Text>Sign In</Card.Text>
          </Card>
        </div>
      );
    };

    return (
      <div>
        <Card className="main" style={styles.desktopMain}>
          <Card.Body>
            {/* Header */}
            <Card.Title>
              <b>
                <Versus fixture={fixture} /> at {convertUTCDateToLocalDateTime(fixture.game_time)}
              </b>
            </Card.Title>
            <OddsTable fixture={fixture} />
            <Pitchers fixture={fixture} />
            <Score fixture={fixture} />

            <Spacer size={20} />

            {/* Suggestions, or a v2 game's verdict */}
            {isV2Game ? (
              <ErrorBoundary name="verdict" resetKey={fixture}>
                <Verdict fixture={fixture} signedIn={isSignedIn} />
              </ErrorBoundary>
            ) : (
              <ErrorBoundary name="suggestions" resetKey={fixture}>
                <Suggestions fixture={fixture} userTier={userTier} />
              </ErrorBoundary>
            )}

            <Spacer size={20} />

            {/* Projections */}
            {renderProjections()}

            <Spacer size={20} />

            {/* Stats Grid */}
            {fixture.recent && (
              <>
                <div style={styles.desktopGrid}>
                  <div><Wins fixture={fixture} /></div>
                  <div><Spreads fixture={fixture} /></div>
                </div>
                <Spacer size={20} />
                <div><PointDiff fixture={fixture} /></div>
                <Spacer size={20} />
                <div style={styles.desktopGrid}>
                  <div><Totals fixture={fixture} /></div>
                  <div><Overs fixture={fixture} /></div>
                </div>
              </>
            )}

            <Spacer size={20} />

            {/* Recents */}
            <div style={styles.desktopGrid}>
              <div>
                <h5 style={{ color: 'white' }}>{fixture.home_team} Recent Games</h5>
                <Recents name={fixture.home_team} date={fixture.game_time} league={fixture.league} compact={true} />
              </div>
              <div>
                <h5 style={{ color: 'white' }}>{fixture.away_team} Recent Games</h5>
                <Recents name={fixture.away_team} date={fixture.game_time} league={fixture.league} compact={true} />
              </div>
            </div>

            <Spacer size={20} />

            {/* H2H | Line Movement */}
            <div style={styles.desktopGrid}>
              <div>
                <HeadtoHead team1={fixture.home_team} team2={fixture.away_team} game={fixture} onClose={null} />
              </div>
              <div>
                <LineMovement fixture={fixture} />
              </div>
            </div>

            <Spacer size={20} />

            {/* Super Summary: v1 only. A v2 game's advocates are in its verdict card. */}
            {!isV2Game && (
              <ErrorBoundary name="supersummary" resetKey={fixture}>
                <SuperSummary fixture={fixture} userTier={userTier} isFixtureUnlocked={true} desktop={true} />
              </ErrorBoundary>
            )}

            <Spacer size={20} />

            {/* Comments */}
            {isSignedIn && (
              <Comments
                fixture={fixture}
                isAuthenticated={isAuthenticated}
                isOpen={isCommentsOpen}
                toggle={() => setIsCommentsOpen(!isCommentsOpen)}
              />
            )}

            <CardButton title="Back" onClick={handleBackToGames} />
            <p style={{ color: 'gray' }}>id: {fixture.id}</p>
          </Card.Body>
        </Card>
      </div>
    );
  }

  else {

  return (
    <div>
      <Card className="main" style={styles.main}>
        <Card.Body>
          <Card.Title>
            <b>
              <Versus fixture={fixture} /> at {convertUTCDateToLocalDateTime(fixture.game_time)}
            </b>
          </Card.Title>
          <OddsTable fixture={fixture} onClick={resetAll} />

          {isButtonListVisible && (<Pitchers fixture={fixture} />)}

          {isButtonListVisible && isV2Game && (
            <ErrorBoundary name="verdict" resetKey={fixture}>
              <Verdict fixture={fixture} signedIn={isSignedIn} />
            </ErrorBoundary>
          )}

            {isButtonListVisible && (
              <CardButton 
                title="Projections"
                onClick={togglePowerRatingsVisibility}
                index={0}
              />
            )}
            {isPowerRatingsVisible && isSignedIn && (
              <Projections fixture={fixture} onClose={togglePowerRatingsVisibility} />
            )}

            {isPowerRatingsVisible && !isSignedIn && (
              <>
                <div style={{
                  marginBottom: "20px",
                  textAlign: "center",
                  padding: "20px"
                }}>
                  <p style={styles.analysis}>
                    Create a free account to unlock projections!
                  </p>
                </div>

                <Card
                  className="cardbutton"
                  style={{...styles.cardbutton, width: '100%', marginBottom: "20px"}}
                  onClick={() => {
                    navigate('/register');
                  }}
                >
                  <Card.Text>Sign Up</Card.Text>
                </Card>

                <Card
                  className="cardbutton"
                  style={{...styles.cardbutton, width: '100%', marginBottom: "20px"}}
                  onClick={() => {
                    navigate('/signin');
                  }}
                >
                  <Card.Text>Sign In</Card.Text>
                </Card>

                <Card
                  className="cardbutton"
                  style={{...styles.cardbutton, width: '100%', marginBottom: "20px"}}
                  onClick={() => {
                    togglePowerRatingsVisibility();
                  }}
                >
                  <Card.Text>Back to fixture</Card.Text>
                </Card>
              </>
            )}
      
            {isButtonListVisible && !isDataVisible && (
              <CardButton 
                title="Stats"
                onClick={toggleDataVisibility}
                index={1}
              />
            )}

            {isDataVisible && (
              <div ref={containerRef} style={{
                overflowY: "scroll",
                height: "100vh",
                scrollSnapType: "y mandatory",
              }}>
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                }}>
                    {isTutorialMode && (
                      <section style={{ ...styles.sectionStyle, minHeight: "120vh", scrollSnapAlign: "start", position: "relative", padding: 0, margin: 0 }}>
                        <ul
                          style={{
                            fontSize: "clamp(12px, calc(12px + (4) * ((100vw - 300px) / (1200 - 320))), 18px)",
                            marginBottom: "20px",
                            textAlign: "left", 
                            listStyleType: "none",
                            paddingLeft: "0",
                            width: "100%",
                          }}>
                          <li>Swipe up to navigate</li>
                        </ul>

                        <div style={{
                          position: "absolute", 
                          bottom: "25vh",
                          left: "50%",
                          transform: "translateX(-50%)",
                          margin: 0,
                          padding: 0,
                        }}>
                          <motion.svg 
                            width="80" 
                            height="80" 
                            viewBox="0 0 24 24"
                            animate={{ 
                              y: [-80, 0, -80],
                            }}
                            transition={{
                              duration: 2,
                              repeat: Infinity,
                              ease: "easeInOut"
                            }}>
                            <path 
                              d="M12 3l8 8h-6v10h-4v-10h-6z"
                              fill="currentColor"
                            />
                          </motion.svg>
                        </div>
                      </section>
                    )}
                    {isTutorialMode && (
                      <section style={{ ...styles.sectionStyle, minHeight: "120vh", scrollSnapAlign: "start", position: "relative", padding: 0, margin: 0 }}>
                      <div style={{
                        marginBottom: "20px",
                        textAlign: "center",
                        padding: "20px"
                      }}>
                    <ul
                      style={{
                        fontSize: "clamp(12px, calc(12px + (4) * ((100vw - 300px) / (1200 - 320))), 18px)",
                        marginBottom: "20px",
                        textAlign: "left",
                        listStyleType: "none",
                        paddingLeft: "0",
                      }}>
                      <li>• Tap anywhere in the first column to toggle between modes</li>
                      <ul style={{ paddingLeft: "20px", listStyleType: "none" }}>
                        <li>◦ Win-Loss mode - show the team's win-loss record</li>
                        <li>◦ Percentage mode - show the win percentage</li>
                        <li>◦ Star mode - for quick evaluation of team performance</li>
                      </ul>
                      <li>
                        • Star ratings
                        <ul style={{ paddingLeft: "20px", listStyleType: "none" }}>
                          <li>◦ ⭐ – Slight advantage</li>
                          <li>◦ ⭐⭐ – Moderate advantage</li>
                          <li>◦ ⭐⭐⭐ – Strong advantage</li>
                        </ul>
                      </li>
                      <li>• How to interpret the data</li>
                      <ul style={{ paddingLeft: "20px", listStyleType: "none" }}>
                        <li>◦ Compare recent performance (last 3, 5, 10 games) to season-long performance</li>
                        <li>◦ Pay attention to home/away performance differences</li>
                      </ul>
                    </ul>
                    </div>
                    </section>
                  )}
                  {isTutorialMode && (
                    <section style={{ ...styles.sectionStyle, minHeight: "120vh", scrollSnapAlign: "start", position: "relative", padding: 0, margin: 0 }}>
                      <div style={{
                        marginBottom: "20px",
                        textAlign: "center",
                        padding: "20px"
                      }}>
                        <p style={styles.analysis}>
                          Done with the tutorial? Tap the button below to disable. You can always re-enable in the sidebar menu or in the profile page.

                          We recommend leaving this on for now since there are additional explanations and tips for each specific section later on.
                        </p>
                      </div>
                      <Card 
                        className="cardbutton" 
                        style={{...styles.cardbutton, width: '100%', marginBottom: "20px"}} 
                        onClick={toggleTutorialMode}
                      >
                        <Card.Text>Disable Tutorial Mode</Card.Text>
                      </Card>
                    </section>
                  )}
                  <section style={{ ...styles.sectionStyle, minHeight: "150vh" }}>
                    <Wins fixture={fixture} />
                  </section>
                  <section style={{ ...styles.sectionStyle, minHeight: "150vh" }}>
                    <Spreads fixture={fixture} />
                  </section>
                  <section style={{ ...styles.sectionStyle, minHeight: "150vh" }}>
                    <PointDiff fixture={fixture} />
                  </section>
                  <section style={{ ...styles.sectionStyle, minHeight: "150vh" }}>
                    <Totals fixture={fixture} />
                  </section>
                  <section style={{ ...styles.sectionStyle, minHeight: "150vh" }}>
                    <Overs fixture={fixture} />
                  </section>
                  <section style={{ ...styles.sectionStyle, minHeight: "150vh" }}>
                    {isTutorialMode && (
                      <ul style={{
                        fontSize: "clamp(12px, calc(12px + (4) * ((100vw - 300px) / (1200 - 320))), 18px)",
                        textAlign: "left",
                        listStyleType: "none",
                        paddingLeft: "0",
                      }}>
                        <li>• You've reached the end of the main stats section. Continue scrolling to see recent games, head-to-head, projections, and comments.</li>
                        <li>• More info:
                          <ul style={{
                            paddingLeft: "20px",
                            listStyleType: "none",
                          }}>
                            <li>◦ The recent games section shows the last 10 games for each team with the elo ratings before each game</li>
                            <li>◦ The head-to-head section shows up to the last 10 matches between the two current</li>
                            <li>◦ The projections section shows our models' recommended plays and win probabilities. Check this after making your decisions to see how it lines up with our own picks. Or start with the recommended picks and work backwards. This is how we do it in our office</li>
                          </ul>
                        </li>
                      </ul>
                    )}
                    <Card className="cardbutton" style={{...styles.cardbutton, width: '100%'}} onClick={toggleDataVisibility}>
                      <Card.Text>Close</Card.Text>
                    </Card>
                  </section>
                </div>
              </div>
            )}

            {isButtonListVisible && (
              <CardButton 
                title={`${fixture.home_team} Recent Games`}
                onClick={toggleRecentsHomeVisibility}
                index={2}
              />
            )}

            {isRecentsHomeVisible && (
              <>
                <Recents name={fixture.home_team} date={fixture.game_time} league={fixture.league} />
                <CardButton 
                  title="Close Recent Games"
                  onClick={toggleRecentsHomeVisibility}
                />
              </>
            )}

            {isButtonListVisible && (
                          <CardButton 
                            title={`${fixture.away_team} Recent Games`}
                            onClick={toggleRecentsAwayVisibility}
                            index={3}
                          />
                        )}

            {isRecentsAwayVisible && (
              <>
                <Recents name={fixture.away_team} date={fixture.game_time} league={fixture.league} />
                <CardButton 
                  title="Close Recent Games"
                  onClick={toggleRecentsAwayVisibility}
                />
              </>
            )}

            {isButtonListVisible && (
              <CardButton 
                title="Head to Head"
                onClick={toggleHeadtoHeadVisibility}
                index={4}
              />
            )}

            {isHeadtoHeadVisible && (
              <HeadtoHead team1={fixture.home_team} team2={fixture.away_team} game={fixture} onClose={toggleHeadtoHeadVisibility} />
            )}

            {isButtonListVisible && (
              <CardButton 
                title="Line Movement"
                onClick={toggleLineMovementVisibility}
                index={5}
              />
            )}

            {isLineMovementVisible && (
              <>
                <LineMovement fixture={fixture} />
                <CardButton 
                  title="Close Line Movement"
                  onClick={toggleLineMovementVisibility}
                />
              </>
            )}

          {isButtonListVisible && (
            <CardButton
              title={`Comments (${commentCount})`}
              onClick={() => {
                if (isSignedIn) {
                  setIsCommentsOpen(true);
                }
              }}
              index={6}
            />
          )}

          <CardButton 
            title="Back"
            onClick={handleBackToGames}
            index={7}
          />
          
          <Spacer size={32} />
          
          {isButtonListVisible &&
            <Comments 
              fixture={fixture} 
              isAuthenticated={isAuthenticated}
              isOpen={isCommentsOpen}
              toggle={() => setIsCommentsOpen(!isCommentsOpen)}
            />
          }
          <Score fixture={fixture} />
          <p>id: {fixture.id}</p>

          {isButtonListVisible && !isV2Game && (
            <ErrorBoundary name="suggestions" resetKey={fixture}>
              <Suggestions fixture={fixture} userTier={userTier} />
            </ErrorBoundary>
          )}
          {isButtonListVisible && !isV2Game && (
            <ErrorBoundary name="supersummary" resetKey={fixture}>
              <SuperSummary fixture={fixture} userTier={userTier} isFixtureUnlocked={true} />
            </ErrorBoundary>
          )}

        </Card.Body>
      </Card>
    </div>
  );
  }
};

const Spacer = ({ size }) => <div style={{ height: size }} />;

export default Games;